(ns ui.view.screening.screening-profile-info-table.model
  (:require
   [common.country :as country]
   [common.localization :as loc]
   [frontend.utils :as fu]))

(defn sanction-label
  [sanction-status]
  (case sanction-status
    :sanction-status/green "Green"
    :sanction-status/yellow "Yellow"
    :sanction-status/red "Red"
    ""))

(defn risk-screen-url
  [external-screening-id]
  (str "https://batch.riskscreen.com/entity/view/" external-screening-id "?pageindex=0&rowsperpage=100"))

(defn company-profile?
  [screening-profile]
  (= (:screening-profile/type screening-profile)
     :screening-profile-type/company))

(defn person-profile?
  [screening-profile]
  (not (company-profile? screening-profile)))

(defn- identity-rows
  [screening-profile]
  (let [{:keys [screening-profile/first-name
                screening-profile/last-name
                screening-profile/date-of-birth
                screening-profile/company-name
                screening-profile/business-id]}
        screening-profile]
    (if (company-profile? screening-profile)
      [{:type  :header
        :label (loc/tr :company-information)}
       {:type  :key-value
        :label (loc/tr :screening-profile/company-name)
        :value company-name}
       {:type  :key-value
        :label (loc/tr :screening-profile/business-id)
        :value business-id}]
      [{:type  :header
        :label (loc/tr :person-information)}
       {:type  :key-value
        :label (loc/tr :screening-profile/name)
        :value (str first-name " " last-name)}
       {:type  :key-value
        :label (loc/tr :screening-profile/date-of-birth)
        :value (when date-of-birth
                 (fu/format-date-ts date-of-birth "." true))}])))

(defn- screening-overview-rows
  [screening-profile]
  (let [{:keys [screening-profile/country
                screening-profile/screening-status]}
        screening-profile]
    [{:type  :key-value
      :label (loc/tr :screening-profile/country)
      :value (country/get-label country)}
     {:type  :header
      :label (loc/tr :screening-information)}
     {:type  :key-value
      :label (loc/tr :screening-profile/screening-status)
      :value (if (= screening-status :screening-status/active)
               (loc/tr :active)
               (loc/tr :inactive))}]))

(defn- sanction-status-row
  [screening-profile]
  (let [sanction-status (:screening-profile/sanction-status screening-profile)
        entity-type-str (if (company-profile? screening-profile)
                          (loc/tr :company-lc)
                          (loc/tr :person-lc))
        end-icon-class (case sanction-status
                         :sanction-status/green "fal fa-check-circle text-[#8ABF4C]"
                         :sanction-status/yellow "fal fa-question-circle text-[#F5BD3E]"
                         :sanction-status/red "fal fa-exclamation-circle text-[#E86546]"
                         nil)
        tooltip (case sanction-status
                  :sanction-status/green
                  (loc/tr :sanction-status-green-info [entity-type-str])

                  :sanction-status/yellow
                  (loc/tr :sanction-status-yellow-info [entity-type-str])

                  :sanction-status/red
                  (loc/tr :sanction-status-red-info [entity-type-str])

                  nil)]
    [{:type           :key-value
      :label          (loc/tr :screening-profile/sanction-status)
      :value          (sanction-label sanction-status)
      :tooltip        tooltip
      :end-icon-class end-icon-class}]))

(defn- pep-rca-rows
  [screening-profile]
  (let [{:keys [screening-profile/pep-status
                screening-profile/latest-pep-status-change
                screening-profile/rca-status
                screening-profile/latest-rca-status-change]}
        screening-profile]
    (when (person-profile? screening-profile)
      [{:type  :key-value
        :label (loc/tr :screening-profile/pep-status)
        :value (if pep-status
                 (loc/tr :true)
                 (loc/tr :false))}
       {:type  :key-value
        :label (loc/tr :screening-profile/latest-pep-status-change)
        :value (when latest-pep-status-change
                 (fu/display-datetime-timestamp latest-pep-status-change))}
       {:type  :key-value
        :label (loc/tr :screening-profile/rca-status)
        :value (if rca-status
                 (loc/tr :true)
                 (loc/tr :false))}
       {:type  :key-value
        :label (loc/tr :screening-profile/latest-rca-status-change)
        :value (when latest-rca-status-change
                 (fu/display-datetime-timestamp latest-rca-status-change))}])))

(defn- metadata-rows
  [screening-profile]
  (let [{:keys [screening-profile/refresh-timestamp
                screening-profile/external-screening-id
                screening-profile/uuid]}
        screening-profile]
    [{:type  :key-value
      :label (loc/tr :screening-profile/refresh-timestamp)
      :value (when refresh-timestamp
               (fu/display-datetime-timestamp refresh-timestamp))}
     {:type  :key-value
      :label (loc/tr :riskscreen-id)
      :value external-screening-id
      :href  (risk-screen-url external-screening-id)}
     {:type  :key-value
      :label (loc/tr :screening-profile/uuid)
      :value uuid}]))

(defn- associated-rows
  [{:keys [show-kyc-data?
           show-counterparty-diligence-data?
           kyc-profiles-and-pep-statuses
           counterparty-diligences-and-pep-statuses]}]
  (concat
   (when (and (seq kyc-profiles-and-pep-statuses)
              show-kyc-data?)
     [{:type  :header
       :label (loc/tr :kyc-profiles)}
      {:type :kyc-profiles}])

   (when (and (seq counterparty-diligences-and-pep-statuses)
              show-counterparty-diligence-data?)
     [{:type  :header
       :label (loc/tr :counterparty-diligences)}
      {:type :counterparty-diligences}])

   (when (and (empty? kyc-profiles-and-pep-statuses)
              show-kyc-data?)
     [{:type  :header
       :label (loc/tr :kyc-profiles)}
      {:type  :info
       :label (loc/tr :no-associated-kyc-profiles-for-screening-profile)}])

   (when (and (empty? counterparty-diligences-and-pep-statuses)
              show-counterparty-diligence-data?)
     [{:type  :header
       :label (loc/tr :counterparty-diligences)}
      {:type  :info
       :label (loc/tr :no-associated-counterparty-diligences-for-screening-profile)}])))

(defn create-table-rows
  [{:keys [screening-profile] :as opts}]
  (->> (concat
        (identity-rows screening-profile)
        (screening-overview-rows screening-profile)
        (sanction-status-row screening-profile)
        (pep-rca-rows screening-profile)
        (metadata-rows screening-profile)
        (associated-rows opts))
       (vec)))
